import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'components/mui/common/Select';
import { ActionModal } from 'components/mui/common/ActionModal';
import { MenuItem } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import type { DashboardItem, TransformedSettingsResponse } from 'types';

interface Props {
  open: boolean;
  onClose: () => void;
  onAdd: (data: DashboardItem) => void;
  length: number;
  isModule?: boolean;
}

const AddDashboardRowModalBase = ({ open, onClose, onAdd, length, isModule = false }: Props) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const dashboards: TransformedSettingsResponse['dashboard'] = useWatch({ name: 'dashboard', control });
  const hasModules = dashboards?.some((item) => item.type === 'modules');

  const options = [
    { value: 'kpiItems', label: t('core.settings.dashboard.kpiItem') },
    { value: 'agendaViewItems', label: t('core.settings.dashboard.agendas') },
  ];

  if (!hasModules && !isModule) {
    options.push({ value: 'modules', label: t('core.settings.dashboard.modules') });
  }

  const [type, setType] = useState<DashboardItem['type'] | ''>('');

  const handleSelectType = useCallback((e: SelectChangeEvent<unknown>) => setType(e.target.value as DashboardItem['type']), []);

  const handleSubmit = useCallback(() => {
    if (type === 'modules') {
      onAdd({ type: 'modules', row: length + 1, viewMode: 'module' });
    } else if (type === 'kpiItems') {
      onAdd({ type: 'kpiItems', row: length + 1, items: [] });
    } else if (type === 'agendaViewItems') {
      onAdd({ type: 'agendaViewItems', row: length + 1, items: [] });
    }
    onClose();
  }, [length, onAdd, onClose, type]);

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={t('common.addRow')}
      submitText={t('common.confirm')}
      onCancel={onClose}
      onSubmit={handleSubmit}
      isOnSubmitDisabled={!type}>
      <Select label={t('common.type')} value={type} onChange={handleSelectType} displayEmpty fullWidth>
        {options.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </ActionModal>
  );
};

export const AddDashboardRowModal = memo(AddDashboardRowModalBase) as typeof AddDashboardRowModalBase;
